import { cs, enUS } from 'date-fns/locale';
import { DetailColumnLayout } from './components/Manager/DetailContent/hooks/useDetailColumnLayout';
import {
  DatasetFormDisplayType,
  DayPreset,
  LayoutCanvas,
  MatchMode,
  RangePreset,
} from './types/types';
import type { FormTokenChainState } from './types/context';
import { ToastPosition } from 'react-hot-toast';

/*USER ACTIONS*/
export const SET_CONFIG = 'setConfig';
export const LOG_IN = 'logIn';
export const LOG_OUT = 'logOut';
export const SET_LANGUAGE = 'setLanguage';
export const START_SESSION = 'startSession';
export const SET_API_ENDPOINT = 'setApiEndpoint';
export const SET_ENDPOINT_CORRECT = 'setEndpointCorrect';
export const UPDATE_LAST_ACTIVITY = 'updateLastActivity';
export const SET_DASHBOARDS = 'setDashboards';
export const SET_DASHBOARD = 'setDashboardDetail';
export const SAVE_REPORTS = 'saveReports';
export const SET_REPORT_FILTER_VALUES = 'setReportFilterValues';
export const SET_GLOBAL_FILTER_VALUES = 'setGlobalFilterValues';
export const SET_ENDPOINT_PAGE_STEP = 'setEndpointPageStep';
export const SET_ENDPOINT_PAGE_MANUAL_ENTERING =
  'setEndpointPageManualEntering';
export const SET_ENDPOINT_ALREADY_ENTERED = 'setEndpointAlreadyEntered';
export const SET_SESSION_ERROR_PENDING = 'setSessionErrorPending';
export const SET_AUTOLOGOUT_PENDING = 'setAutologoutPending';
export const SET_BRANCH_NAME = 'setBranchName';
export const SET_VENUE_CONTEXT = 'setVenueContext';
export const SET_MENU_ITEMS = 'setMenuItems';
export const SET_MENU_ID = 'setMenuId';
export const SET_DEFAULT_CURRENCY_FORMAT = 'setDefaultCurrencyFormat';
export const SET_DEFAULT_NUMBER_FORMAT = 'setDefaultNumberFormat';
export const SET_FORM_TOKENS = 'setFormTokens';
export const SET_FORM_ROOTS = 'setFormRoots';
export const REMOVE_TOKEN = 'removeFormToken';

export const ENGLISH = 'en-US';
export const LANGUAGES = [
  { name: 'Česky', id: 'cs-CZ', dateLocale: cs },
  { name: 'English', id: 'en-US', dateLocale: enUS },
];
export const GRAPH_COLORS = [
  '#D8F1FF',
  '#B8E6FF',
  '#91D6FF',
  '#63C3FF',
  '#3BAFFF',
  '#189BFF',
  '#098DF2',
  '#007FE0',
  '#116EC8',
  '#005FBA',
  '#00519E',
  '#003F91',
  '#022980',
  '#001D5B',
  '#010042',
];

export const RANGE_PRESETS = [
  {
    id: RangePreset.CURRENT_WEEK,
    name: 'currentWeek',
  },
  {
    id: RangePreset.PREVIOUS_WEEK,
    name: 'lastWeek',
  },
  {
    id: RangePreset.CURRENT_MONTH,
    name: 'currentMonth',
  },
  {
    id: RangePreset.PREVIOUS_MONTH,
    name: 'lastMonth',
  },
  {
    id: RangePreset.CURRENT_YEAR,
    name: 'currentYear',
  },
  {
    id: RangePreset.PREVIOUS_YEAR,
    name: 'lastYear',
  },
  // { id: RangePreset.LAST_7_DAYS, name: 'Posledních 7 dnů' },
  // { id: RangePreset.LAST_2_MONTHS, name: 'Poslední 2 měsíce' },
  // { id: RangePreset.LAST_31_DAYS, name: 'Posledních 31 dnů' },
  // { id: RangePreset.TO_NEXT_MONTH_END, name: 'Do konce příštího měsíce' },
  // { id: RangePreset.TO_YEAR_END, name: 'Do konce roku' },
  // { id: RangePreset.RECENT_21_DAYS, name: 'Nejbližších 21 dnů' },
  // { id: RangePreset.NEXT_14_DAYS, name: 'Dalších 14 dnů' },
];
export const DAY_PRESETS = [
  {
    id: DayPreset.CURRENT_DAY,
    name: 'today',
  },
  {
    id: DayPreset.PREVIOUS_DAY,
    name: 'yesterday',
  },
];

export const DATASET_RANGE_PRESETS = [
  {
    id: DayPreset.CURRENT_DAY,
    name: 'today',
  },
  {
    id: DayPreset.PREVIOUS_DAY,
    name: 'yesterday',
  },
  {
    id: DayPreset.TOMORROW,
    name: 'tomorrow',
  },
  {
    id: RangePreset.CURRENT_WEEK,
    name: 'currentWeek',
  },
  {
    id: RangePreset.PREVIOUS_WEEK,
    name: 'lastWeek',
  },
  {
    id: RangePreset.NEXT_WEEK,
    name: 'nextWeek',
  },
  {
    id: RangePreset.CURRENT_MONTH,
    name: 'currentMonth',
  },
  {
    id: RangePreset.PREVIOUS_MONTH,
    name: 'lastMonth',
  },
  {
    id: RangePreset.CURRENT_YEAR,
    name: 'currentYear',
  },
  {
    id: RangePreset.PREVIOUS_YEAR,
    name: 'lastYear',
  },
  { id: RangePreset.LAST_7_DAYS, name: 'last7days' },
];

export const DATASET_DAY_PRESETS = [
  {
    id: DayPreset.CURRENT_DAY,
    name: 'today',
  },
  {
    id: DayPreset.PREVIOUS_DAY,
    name: 'yesterday',
  },
  {
    id: DayPreset.TOMORROW,
    name: 'tomorrow',
  },
];

export const GRID_DATA_LIMIT = 50;
export const GRID_ROW_HEIGHT = 49;

export const SMOOTH_FINAL_LEG_PX = 1000;
export const SMOOTH_DURATION_MS = 800;
export const EXPAND_SCROLL_MS = 400;

export const DETAIL_WIDE_VALUE_CHARS = 150;
// How close to the end of the loaded rows detail stepping starts prefetching.
export const DETAIL_LOAD_MORE_ROW_THRESHOLD = 7;
// Settle window before the detail re-anchors itself onto refreshed data.
export const DETAIL_ROW_SYNC_DEBOUNCE_MS = 100;

export const DETAIL_COLUMN_LAYOUTS: {
  minWidth: number;
  layout: DetailColumnLayout;
}[] = [
  { minWidth: 1500, layout: { count: 6, narrow: 2, wide: 3 } },
  { minWidth: 1100, layout: { count: 4, narrow: 2, wide: 3 } },
  { minWidth: 720, layout: { count: 3, narrow: 4, wide: 6 } },
  { minWidth: 576, layout: { count: 2, narrow: 6, wide: 12 } },
  { minWidth: 0, layout: { count: 1, narrow: 12, wide: 12 } },
];

export const MAX_WIDTH_REFERENCE_PX = 1376;
export const MAX_WIDTH_WINDOW_RATIO = 0.8;
export const COMPACT_BREAKPOINT_PX = 1376;
export const APP_HEADER_BREAKPOINT_PX = 615;
export const APP_HEADER_AND_PADDING_HEIGHT_PX = 105;
export const NARROW_APP_HEADER_DELTA_PX = 24;

export const FILTER_MATCH_MODES = [
  { name: 'endsWith', label: 'Končí', types: ['string'] },
  { name: 'startsWith', label: 'Začíná', types: ['string'] },
  { name: 'custom', label: 'Custom', types: [] },
  { name: 'contains', label: 'Obsahuje', types: ['string'] },
  { name: 'notContains', label: 'Neobsahuje', types: ['string'] },
  {
    name: 'equals',
    label: 'Rovná se',
    types: [
      'string',
      'float',
      'integer',
      'boolean',
      'referenceSingleSelect',
      'radioBtn',
      'combobox',
    ],
  },
  {
    name: 'notEquals',
    label: 'Nerovná se',
    types: [
      'string',
      'float',
      'integer',
      'referenceSingleSelect',
      'radioBtn',
      'combobox',
    ],
  },
  {
    name: 'in',
    label: 'Obsahuje',
    types: ['referenceMultiSelect', 'multiCheckbox'],
  },
  {
    name: 'notIn',
    label: 'Neobsahuje',
    types: ['referenceMultiSelect', 'multiCheckbox'],
  },
  {
    name: 'lt',
    label: 'Menší než',
    types: ['float', 'integer'],
  },
  {
    name: 'lte',
    label: 'Menší nebo rovno',
    types: ['float', 'integer'],
  },
  {
    name: 'gt',
    label: 'Větší než',
    types: ['float', 'integer'],
  },
  {
    name: 'gte',
    label: 'Větší nebo rovno',
    types: ['float', 'integer'],
  },
  { name: 'dateIs', label: 'Je rovno', types: ['date'] },
  { name: 'equals', label: 'Je rovno', types: ['time'] },
  { name: 'lt', label: 'Dříve než', types: ['date', 'time', 'dateTime'] },
  {
    name: 'lte',
    label: 'Nejpozději do',
    types: ['date', 'time', 'dateTime'],
  },
  { name: 'gt', label: 'Později než', types: ['date', 'time', 'dateTime'] },
  {
    name: 'gte',
    label: 'Nejdříve od',
    types: ['date', 'time', 'dateTime'],
  },
  { name: 'dateTo', label: 'Datum nejpozději do', types: ['dateTime'] },
  { name: 'dateFrom', label: 'Datum nejdříve od', types: ['dateTime'] },
  {
    name: 'between',
    label: 'Mezi',
    types: ['float', 'integer', 'date', 'time'],
  },
  { name: 'dateIsNot', label: 'Vše kromě', types: ['date'] },
  { name: 'notEquals', label: 'Vše kromě', types: ['time'] },
  { name: 'datetime-equals', label: 'Je rovno', types: ['dateTime'] },
  {
    name: 'datetime-between',
    label: 'Mezi',
    types: ['dateTime'],
  },
  { name: 'datetime-notEquals', label: 'Vše kromě', types: ['dateTime'] },
  {
    name: 'isEmpty',
    label: 'Je prázdné',
    types: ['string'],
  },
  {
    name: 'isNotEmpty',
    label: 'Není prázdné',
    types: ['string'],
  },
  {
    name: 'isNull',
    label: 'Není zadané',
    types: ['integer', 'float', 'date', 'time', 'dateTime'],
  },
  {
    name: 'isNotNull',
    label: 'Je zadané',
    types: ['integer', 'float', 'date', 'time', 'dateTime'],
  },
];

// isEmpty/isNotEmpty are string-only, isNull/isNotNull cover the numeric and date-like types; a server default from the wrong family maps to its counterpart.
export const EQUIVALENT_VALUELESS_MATCH_MODE: Partial<
  Record<MatchMode, MatchMode>
> = {
  isEmpty: 'isNull',
  isNotEmpty: 'isNotNull',
  isNull: 'isEmpty',
  isNotNull: 'isNotEmpty',
};

export const GRID_DEFAULT_FORM_DISPLAY: DatasetFormDisplayType = {
  formId: null,
  formType: 'insert',
  controlLabel: null,
};

export const HEADER_MIN_HEIGHT_PX = 60;

// HTTP Headers
export const CONTENT_TYPE = 'Content-Type';
export const X_SESSION_TOKEN = 'X-SESSION-TOKEN';
export const X_ASW_FORCE = 'X-ASW-FORCE';
export const X_ASW_WARNING = 'X-ASW-Warning';
export const X_CACHEDQUERY_ID = 'x-cachedquery-id';
export const X_CACHEDQUERY_TOTALCNT = 'x-cachedquery-totalcnt';
export const X_HTTP_METHOD_OVERRIDE = 'X-HTTP-Method-Override';
export const PREFER = 'Prefer';
export const LOCATION = 'location';
export const RETRY_AFTER = 'retry-after';

// Long-running operation support (HTTP 202 + Location/Retry-After polling).
// The poll loop is unbounded: an operation may legitimately run for hours and
// the backend signals a give-up via an error response, not a client-side cap.
export const PREFER_ASYNC_WAIT_SECONDS = 5;
export const PREFER_RESPOND_ASYNC = `respond-async, wait=${PREFER_ASYNC_WAIT_SECONDS}`;
export const LONG_RUNNING_FALLBACK_RETRY_SECONDS = 5;

export const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;
export const STRICT_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
export const TIME_ONLY_FORMATS = ['HH:mm:ss', 'HH:mm'];

export const DATE_INPUT_FORMAT = 'dd. MM. yyyy';
export const TIME_INPUT_FORMAT = 'HH:mm';
export const DATETIME_INPUT_FORMAT = 'dd. MM. yyyy HH:mm';

export const DATE_API_FORMAT = 'yyyy-MM-dd';
export const TIME_API_FORMAT = 'HH:mm:00';
export const DATETIME_API_FORMAT = "yyyy-MM-dd'T'HH:mm:00";

export const DATETIME_MASK_CONFIG = {
  replacement: {
    _: /\d/,
  },
  separate: true,
  showMask: true,
};

export const DATE_MASK = '__. __. ____';
export const DATE_RANGE_MASK = '__. __. ____ - __. __. ____';
export const DATE_TIME_MASK = '__. __. ____ __:__';
export const DATE_TIME_RANGE_MASK = '__. __. ____ __:__ - __. __. ____ __:__';
export const TIME_MASK = '__:__';
export const TIME_RANGE_MASK = '__:__ - __:__';

export const DATE_MASK_OPTIONS = {
  ...DATETIME_MASK_CONFIG,
  mask: DATE_MASK,
};

export const DATE_RANGE_MASK_OPTIONS = {
  ...DATETIME_MASK_CONFIG,
  mask: DATE_RANGE_MASK,
};

export const DATE_TIME_MASK_OPTIONS = {
  ...DATETIME_MASK_CONFIG,
  mask: DATE_TIME_MASK,
};

export const DATE_TIME_RANGE_MASK_OPTIONS = {
  ...DATETIME_MASK_CONFIG,
  mask: DATE_TIME_RANGE_MASK,
};

export const TIME_MASK_OPTIONS = {
  ...DATETIME_MASK_CONFIG,
  mask: TIME_MASK,
};

export const TIME_RANGE_MASK_OPTIONS = {
  ...DATETIME_MASK_CONFIG,
  mask: TIME_RANGE_MASK,
};

export const START_OF_DAY_TIME_VALUES: [number, number, number, number] = [
  0, 0, 0, 0,
];

export const END_OF_DAY_TIME_VALUES: [number, number, number, number] = [
  23, 59, 59, 0,
];

export const TIME_FILTER_DATE_VALUES: [number, number, number] = [2000, 0, 1];
export const TIME_FILTER_DATE_STRING = '2000-01-01T';

export const MAX_SELECTED_LABELS_FOR_MULTISELECT = 3;
export const DEFAULT_LAZY_STATE = {
  first: 0,
  filters: null,
  multiSortMeta: [],
};

export const EMPTY_FORM_TOKEN_CHAIN: FormTokenChainState = {
  nodes: {},
  roots: [],
};

export const FORM_TOKENS_DB_NAME = 'myseptim-form-tokens';
export const FORM_TOKENS_DB_VERSION = 1;
export const FORM_TOKENS_STORE_NAME = 'formTokens';
export const FORM_TOKEN_NODE_KEY_SEPARATOR = '::';

export const DATETIME_CHECKBOX_ID = 'allow-time-checkbox';

export const FORM_TOKEN_EXPIRATION_TIME = 5 * 24 * 60 * 60 * 1000; // 5 days
export const FORM_TOKEN_SMALLER_EXPIRATION_TIME = 1 * 60 * 60 * 1000; // 1 hour
export const FOUR_HOURS_LOOP_TIME = 4 * 60 * 60 * 1000; // 4 hours

export const MAX_SAFE_SIGNIFICANT_DIGITS = 15;

export const MAX_URL_LENGTH = 2048;
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

export const SESSION_ERROR_CODE = 'SESSIONERROR';
export const DATABASE_ERROR_CODE = 'DATABASEERROR';
export const SERVICE_ERROR_CODE = 'SERVICEERROR';
export const MOCKUP_ERROR_CODE = 'MOCKUPERROR';
export const CLOUD_API_ERROR_CODE = 'CLOUDAPIERROR';
export const ASWER_ERROR_CODE = 'ASWER';

export const RESPONSE_STATUS_ACCEPTED = 202;
export const RESPONSE_STATUS_SERVER_UNAVAILABLE = 503;
export const RESPONSE_STATUS_SERVER_TIMEOUT = 504;
export const RESPONSE_STATUS_FORCE = 449;
export const RESPONSE_STATUS_CONTENT_TOO_LARGE = 413;

export const CLIENT_ERROR = 'CLIENT_ERROR';
export const CONTENT_TOO_LARGE_ERROR = 'CONTENT_TOO_LARGE_ERROR';
export const SERVER_ERROR = 'SERVER_ERROR';
export const SERVER_TIMEOUT_ERROR = 'SERVER_TIMEOUT_ERROR';
export const SESSION_ERROR = 'SESSION_ERROR';
export const FORCE_ERROR = 'FORCE_ERROR';
export const ENTRY_ERROR = 'ENTRY_ERROR';
export const DATABASE_ERROR = 'DATABASE_ERROR';
export const API_ERROR = 'API_ERROR';
export const ASWER_ERROR = 'ASWER_ERROR';
// Thrown when a long-running poll loop is aborted because the user navigated
// away (URL change). Carries no user-facing message — it is swallowed silently.
export const LONG_RUNNING_ABORTED_ERROR = 'LONG_RUNNING_ABORTED_ERROR';

// Both the injected CSS file and row classes replace literal backslashes
// with this token so neither side relies on CSS escape rules at runtime.
export const APP_STYLE_BACKSLASH_REPLACEMENT = '__';

export const DEFAULT_CURRENCY_FORMAT = '%.2f';
export const DEFAULT_CURRENCY_PLACEHOLDER = '%m';

// Dataset item keys that may carry a JSONata expression instead of a boolean
export const DATASET_ITEM_VISIBILITY_KEYS = [
  'visible',
  'detailVisible',
] as const;

export const TOAST_POSITION: ToastPosition = 'bottom-center';

/*GRID QUICK FILTER*/
// Tagged on PrimeReact filter menu button so the registry can find each column's overlay trigger by querySelector.
export const QUICK_FILTER_COLUMN_ATTR = 'data-quick-filter-column';

// Tagged on PrimeReact header cell so horizontal smooth-scroll can locate the cell for a given column name.
export const COLUMN_NAME_ATTR = 'data-column-name';

// DOM nodes that own keyboard input and must suppress the quick-filter shortcut so typing inside them does not reopen the filter.
export const INTERACTIVE_TARGET_SELECTOR =
  'input, textarea, select, button, [contenteditable="true"], .p-column-filter-overlay, .modal-window, .popover';

// Window-level event dispatched whenever a column filter opens; mounted FilterTemplate instances close themselves when their column name does not match `detail.except`. Crosses grid boundaries (master ↔ slave) where each grid owns its own ColumnFilterRegistry.
export const CLOSE_OTHER_COLUMN_FILTERS_EVENT =
  'septim:close-other-column-filters';

// Tolerance (px) for treating a remount measurement as back at the held row height.
export const SETTLE_TOLERANCE = 4;

/*DATASET CONTROL DATA*/
// Query param carrying the resolved `data` object a `dataset` control writes
// into the target dataset URL (URI-encoded JSON). The opened dataset exposes
// it to apiMethod templates as the `datasetControl` request property.
export const DATASET_CONTROL_DATA_PARAM = 'data';

export const OPEN_TRIGGER_SELECTOR = '.p-column-filter-menu-button-open';
export const SCROLL_VIEWPORT_SELECTOR =
  '.p-virtualscroller, .p-datatable-wrapper';
// Capture phase: scroll does not bubble.
export const LISTENER_OPTIONS: AddEventListenerOptions = {
  capture: true,
  passive: true,
};
/*LAYOUT EDITOR*/
export const LAYOUT_EDITOR_EMPTY_CANVAS: LayoutCanvas = {
  width: 0,
  height: 0,
  backgroundColor: '#000000',
};

// Size of the very first added object — later ones inherit the size of the
// last selected object (LayoutObjectTemplateContext).
export const LAYOUT_DEFAULT_KEY_SIZE_X = 100;
export const LAYOUT_DEFAULT_KEY_SIZE_Y = 75;

// Defaults for the optional canvas keys: applied on seed (server omitted the key)
// and when the user clears the field in the canvas form. Required keys
// (width/height/backgroundColor) have no default — clearing them keeps the current value.
export const LAYOUT_CANVAS_DEFAULTS = {
  gridX: 0,
  gridY: 0,
  gapX: 0,
  gapY: 0,
  snapGrid: false,
  showPoints: false,
  showVLines: false,
  showHLines: false,
  bestOfScale: false,
  // Percent (100 = 100 %) — the API transports scale in percent (QA-265).
  scaleX: 100,
  scaleY: 100,
  lockItems: false,
} as const;

// Fixed layouts come without a canvas — the grid renders on this FE-only
// canvas (scaled to fit the workspace); its size is never sent to the API.
export const LAYOUT_FIXED_CANVAS_WIDTH = 800;
export const LAYOUT_FIXED_CANVAS_HEIGHT = 600;
// Margin around the fixed grid so the selection border and the copied-source
// ring of the edge buttons don't get clipped by the stage bounds (QA-279).
export const LAYOUT_FIXED_CANVAS_PADDING = 8;

export const LAYOUT_OBJECT_MIN_SIZE = 1;
// Position shift of a pasted object against its clipboard source (cascades
// with repeated pastes).
export const LAYOUT_PASTE_OFFSET = 20;
// Arrow-key move of the selected object when snapGrid is off (with it on the
// object walks the grid boundaries instead).
export const LAYOUT_ARROW_MOVE_STEP = 1;

// Bitmap formats the picture input (control type 'picture') accepts.
export const PICTURE_INPUT_ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

// Smaller drag-drawn rects count as sloppy clicks and create nothing.
export const LAYOUT_DRAW_MIN_SIZE = 5;

// Hard cap of the rendered stage per axis — larger canvases fail to allocate
// in the browser (the crashed-canvas sad face).
export const LAYOUT_MAX_STAGE_PIXELS = 8192;
// Corner badge marking objects that differ from the last loaded/saved state.
export const LAYOUT_OBJECT_CHANGED_BADGE_COLOR = '#ffc409';
export const LAYOUT_OBJECT_NEW_BADGE_COLOR = '#2dd36f';
export const LAYOUT_OBJECT_BADGE_RADIUS = 4;
export const LAYOUT_OBJECT_BADGE_INSET = 8;
export const LAYOUT_OBJECT_SELECTED_STROKE = '#00adee';
export const LAYOUT_OBJECT_SELECTED_STROKE_WIDTH = 3;
export const LAYOUT_OBJECT_DEFAULT_STROKE_WIDTH = 1;
// Dashed frame of the object copied by Ctrl+C (cleared by the next canvas
// click) — drawn OUTSIDE the object box so it stays visible next to the
// selection border.
export const LAYOUT_OBJECT_COPIED_STROKE = '#2dd36f';
export const LAYOUT_OBJECT_COPIED_DASH = [6, 4];
export const LAYOUT_OBJECT_COPIED_FRAME_OFFSET = 5;
export const LAYOUT_OBJECT_COPIED_FRAME_STROKE_WIDTH = 2;
export const LAYOUT_OBJECT_DEFAULT_STROKE = '#ffffff';
export const LAYOUT_OBJECT_DEFAULT_BACKGROUND = '#272727';
export const LAYOUT_OBJECT_DEFAULT_FONT_COLOR = '#ffffff';
export const LAYOUT_OBJECT_DEFAULT_FONT_FAMILY = 'Ubuntu';
export const LAYOUT_OBJECT_DEFAULT_FONT_SIZE = 16;
export const LAYOUT_OBJECT_DEFAULT_TEXT_INDENT = 8;
// DevTools padding-overlay green — indent zone of the focused indent field.
export const LAYOUT_OBJECT_INDENT_OVERLAY_FILL = 'rgba(147, 196, 125, 0.55)';
// Background image rendering when backgroundImageMethod is not set (mirrors
// the definition default).
export const LAYOUT_OBJECT_DEFAULT_BACKGROUND_IMAGE_METHOD = 'center';

// SettingsPanel span for items without explicit width (12 = full panel width).
export const LAYOUT_SETTINGS_PANEL_ITEM_SPAN_FALLBACK = 12;

// Steps of the ▲▼ stepper buttons next to numeric inputs in the settings panel.
export const LAYOUT_FLOAT_FIELD_STEP = 0.1;
export const LAYOUT_INTEGER_FIELD_STEP = 1;
// Holding a stepper button auto-repeats the step: first after the delay, then
// at the interval cadence.
export const LAYOUT_STEPPER_HOLD_DELAY = 400;
export const LAYOUT_STEPPER_HOLD_INTERVAL = 100;

// Initial size of the popout (window.open) canvas window.
export const LAYOUT_POPOUT_WINDOW_WIDTH = 960;
export const LAYOUT_POPOUT_WINDOW_HEIGHT = 640;

// Editor side panel resize bounds: panel never shrinks below MIN, and dragging
// wider always leaves at least WORKSPACE_MIN for the canvas workspace.
export const LAYOUT_PANEL_MIN_WIDTH = 450;
export const LAYOUT_WORKSPACE_MIN_WIDTH = 300;
// Initial panel width as a fraction of the editor content width.
export const LAYOUT_PANEL_INITIAL_WIDTH_RATIO = 0.4;

// Settings panel form columns by the current panel width (mirrors the dataset
// form media-query scale, but driven by the resizable panel width instead of
// the window). Definition item widths assume a 12-column grid. Ordered from
// the widest breakpoint — the first matching entry wins.
export const LAYOUT_PANEL_COLUMN_BREAKPOINTS: {
  minWidth: number;
  columns: number;
}[] = [
  { minWidth: 640, columns: 12 },
  { minWidth: 480, columns: 6 },
  { minWidth: 0, columns: 3 },
];

// Form fields holding a Layouts API hex color (pattern ^#?[0-9A-Fa-f]{6}$) —
// validated client-side before the value reaches the editor state.
export const LAYOUT_HEX_COLOR_FIELDS = ['fontColor', 'backgroundColor'];

// Geometry fields disabled in the object form while the definition mode is 'fixed' —
// the grid cell derives from the array index, per-item geometry is not used.
export const LAYOUT_FIXED_MODE_DISABLED_FIELDS = ['x', 'y', 'width', 'height'];

// Editor-specific item fields disabled in the object form while canvas.lockItems
// is on. Item data (type + reference fields) stay editable by design.
export const LAYOUT_LOCKED_ITEM_FIELDS = [
  'x',
  'y',
  'width',
  'height',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontColor',
  'backgroundColor',
  'backgroundImage',
  'backgroundImageMethod',
  // Septim item parameters keep the desktop uppercase key casing.
  'BUTTONHALIGN',
  'BUTTONVALIGN',
  'BUTTONHINDENT',
  'BUTTONVINDENT',
];

// Optional matrixForm flag: generated buttons skip the items already placed
// on a button of the edited layout.
export const LAYOUT_MATRIX_SKIP_EXISTING_FIELD = 'skipExisting';

// Keys of a matrix generate response that identify the item a button points at.
// They decide what "already placed" means for the skipExisting flag.
export const LAYOUT_MATRIX_IDENTITY_FIELDS = ['stulid', 'rcptid'];

export const LAYOUT_GRID_LINE_COLOR = '#272727';
export const LAYOUT_GRID_DOT_COLOR = '#8f8f8f';
export const LAYOUT_GRID_DOT_RADIUS = 1.4;

// Field handler triggers (shared by dataset forms and the layout editor):
// `change` on text-like controls fires after this pause in typing; other
// controls fire immediately.
export const HANDLER_CHANGE_DEBOUNCE_MS = 1000;
export const HANDLER_TEXT_LIKE_CONTROL_TYPES = new Set([
  'string',
  'text',
  'integer',
  'number',
  'float',
]);

/*TEMPLATES*/
// Single source of the {{ }} placeholder grammar (resolver + params masking).
// Global regex — use with matchAll/replace only, .test() would leak lastIndex.
export const PLACEHOLDER_PATTERN = /{{\s*([\s\S]+?)\s*}}/g;

/*EXTERNAL APP (control action type 'app')*/
// Resolved app url — forgeable, trusted only against the definition's origin.
export const EXTERNAL_APP_URL_PARAM = 'url';
// Control identity (name ?? label) — the page re-derives the trusted origin from it.
export const EXTERNAL_APP_CONTROL_PARAM = 'control';
// Scope the control was pressed in — narrows the lookup and response actions.
export const EXTERNAL_APP_SCOPE_PARAM = 'scope';
// Slave dataset id of a subset-scope control — lets the page refetch its definition after F5.
export const EXTERNAL_APP_SUBSET_PARAM = 'subset';
// Message the page posts INTO the iframe: `{ type, token }` session hand-off.
export const EXTERNAL_APP_TOKEN_MESSAGE = 'app:token';

// The one message the embed may post back — close with optional responseActions.
export const EXTERNAL_APP_CLOSE_MESSAGE = 'app:close';
